import * as React from 'react';
import styled from 'styled-components';
import gettext from '@sdk/lang/gettext';
import formatDate from '@sdk/utils/dateFormat/formatDate';
import getRealDate from '@sdk/utils/getRealDate';
import dateToDayMonth from '@sdk/utils/dateFormat/dateToDayMonth';
import Picker from '@sdk/components/forms/Picker';
import TextField from '@sdk/components/forms/TextField';
import Column from '@sdk/components/ui/spacing/Column';
import Text14 from '@sdk/components/ui/typography/Text14';
import TranslateWithComponents from '@sdk/lang/TranslateWithComponents';
import useAuth from '@sdk/authentication/useAuth';
import Row from '@sdk/components/ui/spacing/Row';
import Tooltip from '@sdk/common/tooltip/Tooltip';
import { LEAVE_CAP_TYPES, LEAVE_CAP_UNITS, PTO_RESET_DATE_OPTIONS } from '@sdk/common/constants';
import { LeaveCapUnit, LeaveResetType } from '@sdk/authentication/types';
import { Feature } from '@sdk/common/features';
import { ThemeColorType } from '@sdk/theme/ThemeColorType';
import type { TimeOffType } from '@sdk/timeOffs/types/TimeOffType';
import type { Sections } from '@sdk/components/ui/anchorMenu/createSections';

import { OutputDateFormatType } from '@web/forms/components/datePicker/datePicker.types';
import DatePickerField from '@web/forms/components/datePicker/DatePickerField/DatePickerField';

import InfoIcon from '../assets/svg/Icons/16/info-gray.svg';
import PremiumGuard from '../premiumGuard/PremiumGuard';
import Padding from '../shifts/ui/Padding';
import usePremiumGuard from '../premiumGuard/usePremiumGuard';
import getPremiumTooltipId from '../premiumGuard/getPremiumTooltipId';
import { PremiumModalNames } from '../premiumGuard/constants/modalNames';

import CollapsibleSettingsSection from './components/CollapsibleSettingsSection';
import RowSpaceBetween from './components/RowSpaceBetween';
import SettingsSection from './components/SettingsSection';
import SettingsSwitcherRow from './components/SettingsSwitcherRow';
import TimeOffTypes from './components/timeOff/TimeOffTypes';
import SettingsHeader from './components/SettingsHeader';
import TimeOffRequest from './components/timeOff/TimeOffRequest';
import { FIELD_WIDTH_205 } from './constants';
import { getLeaveResetDate, isLeaveResetTypeFixed } from './helpers/utils';
import { SetTimeOffTypes, TabType } from './types';
import { useSettingsErrorsContext } from './SettingsErrorContext';
import {
  getLeaveCapFormat,
  getWorkDayHours,
  getZeroEmpty,
  setWorkDayHours,
  setZeroEmpty,
  validateDayCapped,
  validateWorkDayHours,
} from './components/timeOff/helpers/utils';
import { useUserOrgSettings } from './UserOrgSettingsContext';

const StyledTextField = styled(TextField)`
  max-width: 205px;
`;

const StyledRow = styled(Row)`
  align-items: center;
`;

type Props = {
  sectionsConfig: Sections;
  timeOffTypes: TimeOffType[];
  onTimeOffTypesChange: SetTimeOffTypes;
};

const TimeOffScreen: React.FC<React.PropsWithChildren<Props>> = ({
  sectionsConfig,
  timeOffTypes,
  onTimeOffTypesChange,
}) => {
  const { userOrgSettings, onUserOrgSettingsChange } = useUserOrgSettings();
  const { isOrgFeatureEnabled, currentUser } = useAuth();
  const { getPremiumGuardConfig, tooltipRefs } = usePremiumGuard();
  const { toggleError } = useSettingsErrorsContext();

  return (
    <>
      <SettingsSection>
        <SettingsHeader
          ref={sectionsConfig.refs.timeOffRequests}
          withSeparator
          title={gettext('Time off requests')}
        />
        <TimeOffRequest />
      </SettingsSection>

      <PremiumGuard
        premiumGuardModalName={PremiumModalNames.paidTimeOffs}
        premiumFeature={Feature.leaveTypes}
        hasAccess={isOrgFeatureEnabled(Feature.leaveTypes)}
        tooltipRef={tooltipRefs[getPremiumTooltipId(Feature.leaveTypes)]}>
        <CollapsibleSettingsSection
          withSeparator
          isActive={userOrgSettings.leaveTypesEnabled}
          headerItem={
            <>
              <SettingsHeader
                ref={sectionsConfig.refs.paidTimeOff}
                withSeparator
                title={gettext('Paid time off')}
                premiumGuard={getPremiumGuardConfig(Feature.leaveTypes)}
              />
              <SettingsSwitcherRow
                disabled={!currentUser.isAdmin}
                subtitle={gettext('Enable paid time off and set caps for the year.')}
                value={userOrgSettings.leaveTypesEnabled}
                onChange={(value): void => onUserOrgSettingsChange({ leaveTypesEnabled: value })}
              />
            </>
          }
          contentItem={
            <Column>
              <CollapsibleSettingsSection
                isActive={isLeaveResetTypeFixed(userOrgSettings.leaveResetType)}
                headerItem={
                  <RowSpaceBetween pBottom={20}>
                    <Column>
                      <Text14 semiBold themeColor={ThemeColorType.gray}>
                        {gettext('Reset date')}
                      </Text14>
                      <Text14 themeColor={ThemeColorType.gray}>
                        {gettext('Indicates when PTO resets for the year.')}
                      </Text14>
                    </Column>
                    <StyledRow>
                      {!isLeaveResetTypeFixed(userOrgSettings.leaveResetType) ? (
                        <Padding right={10}>
                          <InfoIcon
                            data-place="top"
                            data-tip={gettext(
                              'If employee does not have a hire date, January 1 will be used as default.'
                            )}
                          />
                        </Padding>
                      ) : null}
                      <Picker
                        customWidth={FIELD_WIDTH_205}
                        disabled={!currentUser.isAdmin}
                        options={PTO_RESET_DATE_OPTIONS}
                        value={userOrgSettings.leaveResetType}
                        onChange={(value: LeaveResetType): void =>
                          onUserOrgSettingsChange({ leaveResetType: value })
                        }
                      />
                    </StyledRow>
                    {!isLeaveResetTypeFixed(userOrgSettings.leaveResetType) ? (
                      <Tooltip attachToBody />
                    ) : null}
                  </RowSpaceBetween>
                }
                contentItem={
                  <RowSpaceBetween pBottom={20}>
                    <Text14 themeColor={ThemeColorType.gray}>
                      <TranslateWithComponents
                        replaceComponents={{
                          component: (
                            <Text14 themeColor={ThemeColorType.primary}>
                              {dateToDayMonth(getLeaveResetDate(userOrgSettings.leaveResetDate))}
                            </Text14>
                          ),
                        }}>
                        {gettext('PTO cap starts on [[component]].')}
                      </TranslateWithComponents>
                    </Text14>
                    <Column>
                      <DatePickerField
                        customWidth={FIELD_WIDTH_205}
                        withoutYearPicker
                        outputFormat={OutputDateFormatType.ddMMMM}
                        date={getRealDate(userOrgSettings.leaveResetDate)}
                        onDateChange={(date: Date): void =>
                          onUserOrgSettingsChange({ leaveResetDate: formatDate(date) })
                        }
                        disabled={!currentUser.isAdmin}
                      />
                    </Column>
                  </RowSpaceBetween>
                }
              />
              <CollapsibleSettingsSection
                isActive={Boolean(userOrgSettings.useGlobalLeaveCap)}
                headerItem={
                  <RowSpaceBetween pBottom={20}>
                    <Text14 semiBold themeColor={ThemeColorType.gray}>
                      {gettext('Cap paid time off')}
                    </Text14>
                    <Picker
                      customWidth={FIELD_WIDTH_205}
                      disabled={!currentUser.isAdmin || !currentUser.org.isPremium}
                      options={LEAVE_CAP_TYPES}
                      value={userOrgSettings.useGlobalLeaveCap ? 1 : 0}
                      onChange={(value: number): void =>
                        onUserOrgSettingsChange({ useGlobalLeaveCap: Boolean(value) })
                      }
                    />
                  </RowSpaceBetween>
                }
                contentItem={
                  <RowSpaceBetween pBottom={20}>
                    <Text14 themeColor={ThemeColorType.gray}>
                      {gettext('Total number of %(capped)s capped', {
                        capped: getLeaveCapFormat(userOrgSettings.leaveCapUnit).toLowerCase(),
                      })}
                    </Text14>
                    <StyledTextField
                      disabled={!currentUser.isAdmin || !currentUser.org.isPremium}
                      placeholder={gettext('Unlimited')}
                      postfix={gettext(
                        getLeaveCapFormat(userOrgSettings.leaveCapUnit).toLowerCase()
                      )}
                      value={setZeroEmpty(userOrgSettings.leaveCap)}
                      onChange={(e): void => {
                        onUserOrgSettingsChange({
                          leaveCap: Number(getZeroEmpty(e.currentTarget.value)) || 0,
                        });
                        toggleError(
                          TabType.timeOff,
                          'leaveCap',
                          !validateDayCapped(Number(getZeroEmpty(e.currentTarget.value)) || 0)
                        );
                      }}
                      invalid={!validateDayCapped(userOrgSettings.leaveCap)}
                    />
                  </RowSpaceBetween>
                }
              />

              <SettingsSection withSeparator>
                <RowSpaceBetween pBottom={20}>
                  <Text14 themeColor={ThemeColorType.gray}>
                    <TranslateWithComponents
                      replaceComponents={{
                        component: (
                          <Text14 themeColor={ThemeColorType.primary}>
                            {getWorkDayHours(userOrgSettings.workDayMinutes) || 'X'}
                          </Text14>
                        ),
                      }}>
                      {gettext('Maximum paid hours for a full day is [[component]] hours.')}
                    </TranslateWithComponents>
                  </Text14>
                  <StyledTextField
                    disabled={!currentUser.isAdmin || !currentUser.org.isPremium}
                    postfix={gettext('Hours')}
                    value={getWorkDayHours(userOrgSettings.workDayMinutes) || 0}
                    onChange={(e): void => {
                      onUserOrgSettingsChange({
                        workDayMinutes: setWorkDayHours(e.currentTarget.value) || 0,
                      });
                      validateWorkDayHours(e.currentTarget.value);
                    }}
                    invalid={
                      !validateWorkDayHours(getWorkDayHours(userOrgSettings.workDayMinutes) || 0)
                    }
                    error={gettext('Must be a number in range [1..24]')}
                  />
                </RowSpaceBetween>

                <RowSpaceBetween>
                  <Text14 themeColor={ThemeColorType.gray}>{gettext('Cap format:')}</Text14>
                  <Picker
                    customWidth={FIELD_WIDTH_205}
                    disabled={!currentUser.isAdmin || !currentUser.org.isPremium}
                    options={LEAVE_CAP_UNITS}
                    value={userOrgSettings.leaveCapUnit}
                    onChange={(value: LeaveCapUnit): void =>
                      onUserOrgSettingsChange({ leaveCapUnit: value })
                    }
                  />
                </RowSpaceBetween>
              </SettingsSection>

              {userOrgSettings.leaveTypesEnabled ? (
                <TimeOffTypes
                  timeOffTypes={timeOffTypes}
                  useGlobalLeaveCap={userOrgSettings.useGlobalLeaveCap}
                  onTimeOffTypesChange={onTimeOffTypesChange}
                />
              ) : null}
            </Column>
          }
        />
      </PremiumGuard>
    </>
  );
};

export default TimeOffScreen;
